import React, { useState } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CK_CONFIGS } from "configs/ckEditor";
import validate from "validate.js";

const constraints = {
  name: {
    presence: { allowEmpty: false, message: "^Name is required" }
  },
  provider: {
    presence: { allowEmpty: false, message: "^Provider is required" }
  },
  account_id: {
    presence: { allowEmpty: false, message: "^Account ID is required" }
  },
  account_no: {
    presence: { allowEmpty: false, message: "^Account Number is required" }
  },
  client_id: {
    presence: { allowEmpty: false, message: "^Client ID is required" }
  },
  client_secret: {
    presence: { allowEmpty: false, message: "^Client Secret is required" }
  },
  description: {
    presence: { allowEmpty: true, message: "^Description is required" }
  }
};

const ACDIntegrationModal = ({ show, handleClose, onAdd }) => {
  const [formState, setFormState] = useState({
    name: "",
    provider: "",
    account_id: "",
    account_no: "",
    client_id: "",
    client_secret: "",
    status: false,
    description: false
  });
  const [errors, setErrors] = useState({});

  const hasError = (field) => errors[field];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleDescriptionChange = (event, editor) => {
    const data = editor.getData();
    setFormState((prev) => ({
      ...prev,
      description: data
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validate(formState, constraints) || {};
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      // Call parent handler to add integration
      if (onAdd) onAdd({
        ...formState,
        status: formState.status ? "Active" : "Inactive"
      });
      setFormState({
        name: "",
        provider: "",
        account_id: "",
        account_no: "",
        client_id: "",
        client_secret: "",
        status: false,
        description: ""
      });
      setErrors({});
    }
  };

  const isFormInvalid = !!validate(formState, constraints);

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <h3 className="font-16 font-weight-bold heading-color">Add New ACD Integration</h3>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label className="label-input">Name</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  placeholder="Type a drop down name"
                  className="input-field"
                  value={formState.name}
                  onChange={handleChange}
                  isInvalid={!!hasError("name")}
                />
                {hasError("name") && (
                  <Form.Text className="text-danger">
                    {errors.name[0]}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="label-input">Provider</Form.Label>
                <Form.Control
                  type="text"
                  name="provider"
                  placeholder="Type a provider"
                  className="input-field"
                  value={formState.provider}
                  onChange={handleChange}
                  isInvalid={!!hasError("provider")}
                />
                {hasError("provider") && (
                  <Form.Text className="text-danger">
                    {errors.provider[0]}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="label-input">Account ID</Form.Label>
                <Form.Control
                  type="text"
                  name="account_id"
                  placeholder="Type a ID"
                  className="input-field"
                  value={formState.account_id}
                  onChange={handleChange}
                  isInvalid={!!hasError("account_id")}
                />
                {hasError("account_id") && (
                  <Form.Text className="text-danger">
                    {errors.account_id[0]}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label className="label-input">Account Number</Form.Label>
                <Form.Control
                  type="text"
                  name="account_no"
                  placeholder="Type a number"
                  className="input-field"
                  value={formState.account_no}
                  onChange={handleChange}
                  isInvalid={!!hasError("account_no")}
                />
                {hasError("account_no") && (
                  <Form.Text className="text-danger">
                    {errors.account_no[0]}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="label-input">Client ID</Form.Label>
                <Form.Control
                  type="text"
                  name="client_id"
                  placeholder="Type a Client ID"
                  className="input-field"
                  value={formState.client_id}
                  onChange={handleChange}
                  isInvalid={!!hasError("client_id")}
                />
                {hasError("client_id") && (
                  <Form.Text className="text-danger">
                    {errors.client_id[0]}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="label-input">Client Secret</Form.Label>
                <Form.Control
                  type="text"
                  name="client_secret"
                  placeholder="Type a secret"
                  className="input-field"
                  value={formState.client_secret}
                  onChange={handleChange}
                  isInvalid={!!hasError("client_secret")}
                />
                {hasError("client_secret") && (
                  <Form.Text className="text-danger">
                    {errors.client_secret[0]}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3 align-items-center">
            <Col md={2}>
              <div className="d-flex">
                <Form.Check
                  type="switch"
                  id="status-switch"
                  name="status"
                  checked={formState.status}
                  onChange={handleChange}
                />
                <Form.Label className="label-input mb-0">Status</Form.Label>
              </div>
            </Col>
          </Row>

          <Form.Group>
            <Form.Label className="label-input">Description</Form.Label>
            <div>
              <CKEditor
                editor={ClassicEditor}
                config={CK_CONFIGS(localStorage.getItem("token"))}
                data={formState.description}
                onChange={handleDescriptionChange}
              />
              {hasError("description") && (
                <Form.Text className="text-danger">
                  {errors.description[0]}
                </Form.Text>
              )}
            </div>
          </Form.Group>
          <Modal.Footer className="d-inline">
            <div className="d-flex align-items-center justify-content-end gap-3 w-100">
              <button variant="secondary" className="c-b-btn w-100" onClick={handleClose}>
                Cancel
              </button>
              <button
                type="submit"
                variant="primary"
                className="n-u-btn w-100"
                disabled={isFormInvalid}
              >
                Add
              </button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ACDIntegrationModal;
